import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BrandMark, useBrand, useNavConfig } from "@/components/politics/brand";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { suffix: brandSuffix, color: brandColor } = useBrand();
  const nav = useNavConfig();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fecha o menu mobile ao trocar de rota
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ease-out ${
        scrolled
          ? "bg-[#F5FAFB]/85 backdrop-blur-xl border-b border-[#1B1D1D]/8"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <Link
          to="/politics"
          data-testid="header-logo"
          className="flex items-center gap-2.5 group"
          aria-label="RSM — Início"
        >
          <BrandMark
            size={32}
            className="transition-transform duration-300 group-hover:scale-105"
          />
          <span className="font-display text-base font-semibold tracking-tight text-[#1B1D1D]">
            RSM <span style={{ color: brandColor }}>{brandSuffix}</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {nav.items.map((item) => (
            <Link
              key={item.id}
              to={`${nav.base}#${item.id}`}
              data-testid={`nav-link-${item.id}`}
              className="text-sm font-medium text-[#51565F] hover:text-[#1B1D1D] transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {nav.secondary && (
            <Link
              to={nav.secondary.to}
              data-testid="header-secondary-link"
              className="text-sm font-medium text-[#51565F] hover:text-[#7E20FF] transition-colors"
            >
              {nav.secondary.label}
            </Link>
          )}
          {nav.cta && (
            <Link
              to={nav.cta.to}
              data-testid="header-cta"
              style={{ backgroundColor: brandColor }}
              className="inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:brightness-95"
            >
              {nav.cta.label}
            </Link>
          )}
        </div>

        <button
          data-testid="header-mobile-toggle"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          onClick={() => setOpen(!open)}
          className="md:hidden grid h-10 w-10 place-items-center rounded-lg border border-[#1B1D1D]/15 text-[#1B1D1D]"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="header-mobile-panel"
          className="md:hidden border-t border-[#1B1D1D]/8 bg-[#F5FAFB]/95 backdrop-blur-xl"
        >
          <div className="flex flex-col gap-1 px-6 py-4">
            {nav.items.map((item) => (
              <Link
                key={item.id}
                to={`${nav.base}#${item.id}`}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${item.id}`}
                className="rounded-lg px-3 py-2 text-sm font-medium text-[#1B1D1D] hover:bg-[#208BFF]/8"
              >
                {item.label}
              </Link>
            ))}
            {nav.secondary && (
              <Link
                to={nav.secondary.to}
                onClick={() => setOpen(false)}
                data-testid="header-mobile-secondary"
                className="rounded-lg px-3 py-2 text-sm font-medium text-[#7E20FF]"
              >
                {nav.secondary.label}
              </Link>
            )}
            {nav.cta && (
              <Link
                to={nav.cta.to}
                onClick={() => setOpen(false)}
                data-testid="header-mobile-cta"
                style={{ backgroundColor: brandColor }}
                className="mt-2 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-white"
              >
                {nav.cta.label}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
